import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RegionalHierarchicalTable } from "@/components/charts/RegionalHierarchicalTable";
import { TrendingUp, TrendingDown, Twitter, Eye, DollarSign, Users, Target, Youtube, MessageCircle, Globe } from "lucide-react";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { useTheme } from "@/contexts/ThemeContext";
import { TwitterCampaignList } from "@/components/twitter/TwitterCampaignList";
import TwitterCampaignDetail from "@/components/twitter/TwitterCampaignDetail";
import { TwitterKolDashboard } from "@/components/twitter/TwitterKolDashboard";
import { TwitterKolList } from "@/components/twitter/TwitterKolList";
import { useAuth } from "@/contexts/AuthContext";
import { TopProgressBar } from "@/components/ui/top-progress-bar";

interface FetchProgress {
  current: number;
  total: number;
  estimatedTotal?: number;
}

interface CounterCardProps {
  title: string;
  value: string;
  change: number;
  increaseAmount: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const CounterCard = ({ title, value, change, increaseAmount, icon, color, bgColor }: CounterCardProps) => {
  const [displayValue, setDisplayValue] = useState("0");
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setDisplayValue(value);
      setIsAnimating(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <Card className={`${bgColor} border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-white/80 mb-1">{title}</p>
            <div className={`text-3xl font-bold text-white transition-all duration-500 ${isAnimating ? 'scale-110' : 'scale-100'}`}>
              {displayValue}
            </div>
            <div className="flex items-center mt-2 space-y-1">
              {change > 0 ? (
                <div className="flex items-center text-white/90">
                  <TrendingUp className="mr-1 h-4 w-4" />
                  <span className="text-sm font-medium">+{change.toFixed(1)}%</span>
                </div>
              ) : (
                <div className="flex items-center text-white/90">
                  <TrendingDown className="mr-1 h-4 w-4" />
                  <span className="text-sm font-medium">{change.toFixed(1)}%</span>
                </div>
              )}
              <div className="text-white/80 text-xs ml-2">
                ({increaseAmount})
              </div>
            </div>
          </div>
          <div className={`${color} p-4 rounded-2xl bg-white/20`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface SmallCounterCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  bgColor: string;
}

const SmallCounterCard = ({ title, value, icon, bgColor }: SmallCounterCardProps) => {
  const [displayValue, setDisplayValue] = useState("0");
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setDisplayValue(value);
      setIsAnimating(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <Card className={`${bgColor} border-0 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-white/80 mb-1">{title}</p>
            <div className={`text-xl font-bold text-white transition-all duration-500 ${isAnimating ? 'scale-110' : 'scale-100'}`}>
              {displayValue}
            </div>
          </div>
          <div className="p-2 rounded-xl bg-white/20">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const GlobalReach = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [fetchProgress, setFetchProgress] = useState<FetchProgress | null>(null);
  const [kolData, setKolData] = useState<any[]>([]);
  const [timeFilter, setTimeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("2025");
  const [monthFilter, setMonthFilter] = useState("all");
  const [activeTwitterView, setActiveTwitterView] = useState<"list" | "detail">("list");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [twitterTab, setTwitterTab] = useState<"dashboard" | "kolList" | "campaigns">("dashboard");
  const { toast } = useToast();
  const { theme } = useTheme();
  const { hasFullAccess } = useAuth();
  const isLightMode = theme === 'light';
  
  // Enhanced function to fetch all KOL tracker data using batch fetching
  const fetchAllKolTrackerData = async () => {
    try {
      console.log("Starting to fetch all KOL tracker data with batch mechanism...");
      
      // First, get the exact count
      const { count, error: countError } = await supabase
        .from('kol_tracker')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      const totalRecords = count || 0;
      console.log(`Total records in kol_tracker table: ${totalRecords}`);

      if (totalRecords === 0) {
        return [];
      }

      setFetchProgress({
        current: 0,
        total: Math.ceil(totalRecords / 1000),
        estimatedTotal: totalRecords
      });

      const batchSize = 1000;
      const totalBatches = Math.ceil(totalRecords / batchSize);
      let allData: any[] = [];

      // Fetch data in batches
      for (let batch = 0; batch < totalBatches; batch++) {
        const from = batch * batchSize;
        const to = from + batchSize - 1;

        console.log(`Fetching batch ${batch + 1}/${totalBatches} (records ${from} to ${to})`);

        const { data: batchData, error } = await supabase
          .from('kol_tracker')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, to);

        if (error) {
          console.error(`Error fetching batch ${batch + 1}:`, error);
          throw error;
        }

        if (batchData) {
          allData = [...allData, ...batchData];
          console.log(`Batch ${batch + 1} fetched: ${batchData.length} records. Total so far: ${allData.length}`);
        }

        // Update progress
        setFetchProgress({
          current: batch + 1,
          total: totalBatches,
          estimatedTotal: totalRecords
        });

        // Add a small delay to prevent overwhelming the database
        if (batch < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`Successfully fetched all data. Total records: ${allData.length}`);
      console.log(`Expected: ${totalRecords}, Actual: ${allData.length}`);
      
      // Verify the count matches
      if (allData.length !== totalRecords) {
        console.warn(`Data count mismatch! Expected: ${totalRecords}, Got: ${allData.length}`);
      }

      return allData;

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setFetchProgress(null);
        
        const data = await fetchAllKolTrackerData();
        
        console.log("Final data loaded:", data?.length || 0, "records");
        setKolData(data || []);
      } catch (err) {
        console.error("Error fetching KOL data:", err);
        toast({
          title: "Error",
          description: "Failed to load global reach data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
        setFetchProgress(null);
      }
    };
    
    fetchData();
  }, [toast]);

  const filteredData = useMemo(() => {
    if (!kolData.length) return [];
    
    let filtered = [...kolData];
    
    if (timeFilter !== "all") {
      const now = new Date();
      let startDate: Date;
      
      switch (timeFilter) {
        case "1m":
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        case "3m":
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        case "6m":
          startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          break;
        case "1y":
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        default:
          return filtered;
      }
      
      filtered = filtered.filter(item => {
        if (!item.posting_date) return false;
        const itemDate = new Date(item.posting_date);
        return itemDate >= startDate;
      });
    }
    
    if (yearFilter !== "all") {
      const year = parseInt(yearFilter);
      filtered = filtered.filter(item => {
        if (!item.posting_date) return false;
        const itemDate = new Date(item.posting_date);
        return itemDate.getFullYear() === year;
      });
    }
    
    if (monthFilter !== "all") {
      const month = parseInt(monthFilter);
      filtered = filtered.filter(item => {
        if (!item.posting_date) return false;
        const itemDate = new Date(item.posting_date);
        return itemDate.getMonth() === month - 1;
      });
    }
    
    return filtered;
  }, [kolData, timeFilter, yearFilter, monthFilter]);

  const getCompactStatistic = (data: any[], accessor: (item: any) => number) => {
    const total = data.reduce((sum, item) => sum + (accessor(item) || 0), 0);
    return new Intl.NumberFormat().format(total);
  };

  const getPlatformStats = () => {
    console.log("Calculating platform stats from filtered data:", filteredData.length, "records");
    
    const twitterData = filteredData.filter(item => item.platform?.toLowerCase() === 'twitter');
    const youtubeData = filteredData.filter(item => item.platform?.toLowerCase() === 'youtube');
    const telegramData = filteredData.filter(item => item.platform?.toLowerCase() === 'telegram');
    const otherData = filteredData.filter(item => {
      const platform = item.platform?.toLowerCase();
      return platform && !['twitter', 'youtube', 'telegram'].includes(platform);
    });

    console.log("Platform breakdown:", {
      twitter: twitterData.length,
      youtube: youtubeData.length,
      telegram: telegramData.length,
      others: otherData.length,
      total: twitterData.length + youtubeData.length + telegramData.length + otherData.length
    });

    return {
      twitter: {
        kols: new Set(twitterData.map(item => item.kol_db_id)).size,
        posts: twitterData.length
      },
      youtube: {
        kols: new Set(youtubeData.map(item => item.kol_db_id)).size,
        posts: youtubeData.length
      },
      telegram: {
        kols: new Set(telegramData.map(item => item.kol_db_id)).size,
        posts: telegramData.length
      },
      others: {
        kols: new Set(otherData.map(item => item.kol_db_id)).size,
        posts: otherData.length
      }
    };
  };

  const platformStats = getPlatformStats();

  const businessUnits = [
    'MNT Token Mindshare', 
    'Rewards Station', 
    'mETH', 
    'MantleX', 
    'Mantle Network', 
    'Mantle Group', 
    'Mantle Banking',
    'Index Fund',
    'FBTC',
    'Other'
  ];
  
  const years = useMemo(() => {
    if (!kolData.length) return [];
    
    const yearsSet = new Set<number>();
    kolData.forEach(item => {
      if (item.posting_date) {
        const date = new Date(item.posting_date);
        yearsSet.add(date.getFullYear());
      }
    });
    
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [kolData]);

  const getCurrentAndPreviousMonthStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Previous month calculation
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    const currentMonthData = kolData.filter(item => {
      if (!item.posting_date) return false;
      const date = new Date(item.posting_date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    const previousMonthData = kolData.filter(item => {
      if (!item.posting_date) return false;
      const date = new Date(item.posting_date);
      return date.getMonth() === prevMonth && date.getFullYear() === prevYear;
    });
    
    const currentReach = currentMonthData.reduce((sum, item) => sum + (item.content_views || 0), 0);
    const prevReach = previousMonthData.reduce((sum, item) => sum + (item.content_views || 0), 0);
    const reachChange = prevReach ? ((currentReach - prevReach) / prevReach) * 100 : 0;
    const reachIncrease = currentReach - prevReach;
    
    const currentInvestment = currentMonthData.reduce((sum, item) => sum + (item.expense || 0), 0);
    const prevInvestment = previousMonthData.reduce((sum, item) => sum + (item.expense || 0), 0);
    const investmentChange = prevInvestment ? ((currentInvestment - prevInvestment) / prevInvestment) * 100 : 0;
    const investmentIncrease = currentInvestment - prevInvestment;

    const currentKols = new Set(currentMonthData.map(item => item.kol_db_id)).size;
    const prevKols = new Set(previousMonthData.map(item => item.kol_db_id)).size;
    const kolsChange = prevKols ? ((currentKols - prevKols) / prevKols) * 100 : 0;
    const kolsIncrease = currentKols - prevKols;

    const currentPostings = currentMonthData.length;
    const prevPostings = previousMonthData.length;
    const postingsChange = prevPostings ? ((currentPostings - prevPostings) / prevPostings) * 100 : 0;
    const postingsIncrease = currentPostings - prevPostings;
    
    return {
      reach: {
        current: currentReach,
        previous: prevReach,
        percentChange: reachChange,
        increaseAmount: reachIncrease
      },
      investment: {
        current: currentInvestment,
        previous: prevInvestment,
        percentChange: investmentChange,
        increaseAmount: investmentIncrease
      },
      kols: {
        current: currentKols,
        previous: prevKols,
        percentChange: kolsChange,
        increaseAmount: kolsIncrease
      },
      postings: {
        current: currentPostings,
        previous: prevPostings,
        percentChange: postingsChange,
        increaseAmount: postingsIncrease
      }
    };
  };

  const monthlyStats = getCurrentAndPreviousMonthStats();

  const handleCampaignSelect = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setActiveTwitterView("detail");
  };

  const handleBackToList = () => {
    setActiveTwitterView("list");
    setSelectedCampaignId(null);
  };

  return (
    <div className="space-y-6 animate-fade-in relative p-6 w-full max-w-full">
      {/* Progress Bar */}
      {fetchProgress && (
        <TopProgressBar
          isVisible={true}
          current={fetchProgress.current}
          total={fetchProgress.total}
          title={`Fetching Global Reach Data (${fetchProgress.estimatedTotal || 'Unknown'} total records)`}
        />
      )}

      {isLoading && <TableLoadingOverlay isLoading={isLoading} message="Loading global reach data..." />}
      
      <div className="border border-border rounded-md overflow-hidden">
        <Tabs defaultValue="global-reach" className="w-full">
          <TabsList className="w-full justify-start rounded-none bg-transparent mb-0 px-0 h-auto border-b border-border">
            <TabsTrigger 
              value="global-reach" 
              className="rounded-none px-8 py-3 text-base font-semibold"
            >
              Global Reach
            </TabsTrigger>
            {hasFullAccess && (
              <TabsTrigger 
                value="twitter-reach" 
                className="rounded-none px-8 py-3 text-base font-semibold flex items-center gap-2"
              >
                <Twitter size={18} /> X (Twitter) Reach
              </TabsTrigger>
            )}
          </TabsList>

          <div className="p-6">
            <TabsContent value="global-reach" className="mt-0">
              <div className="flex items-center justify-end mb-4">
                <div className="flex items-center gap-4">
                  <Select value={timeFilter} onValueChange={setTimeFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select time period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="1m">Last Month</SelectItem>
                      <SelectItem value="3m">Last 3 Months</SelectItem>
                      <SelectItem value="6m">Last 6 Months</SelectItem>
                      <SelectItem value="1y">Last Year</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={yearFilter} onValueChange={setYearFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      {years.map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={monthFilter} onValueChange={setMonthFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      <SelectItem value="1">January</SelectItem>
                      <SelectItem value="2">February</SelectItem>
                      <SelectItem value="3">March</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="5">May</SelectItem>
                      <SelectItem value="6">June</SelectItem>
                      <SelectItem value="7">July</SelectItem>
                      <SelectItem value="8">August</SelectItem>
                      <SelectItem value="9">September</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                      <SelectItem value="11">November</SelectItem>
                      <SelectItem value="12">December</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Main Vibrant Counter Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <CounterCard
                    title="Total Reach"
                    value={isLoading ? "..." : getCompactStatistic(filteredData, item => item.content_views || 0)}
                    change={monthlyStats.reach.percentChange}
                    increaseAmount={monthlyStats.reach.increaseAmount >= 0 ? `+${new Intl.NumberFormat().format(monthlyStats.reach.increaseAmount)}` : new Intl.NumberFormat().format(monthlyStats.reach.increaseAmount)}
                    icon={<Eye className="h-8 w-8 text-white" />}
                    color="text-blue-500"
                    bgColor="bg-gradient-to-br from-blue-500 to-blue-600"
                  />
                  
                  <CounterCard
                    title="Total Investment"
                    value={isLoading ? "..." : "$" + getCompactStatistic(filteredData, item => item.expense || 0)}
                    change={monthlyStats.investment.percentChange}
                    increaseAmount={monthlyStats.investment.increaseAmount >= 0 ? `+$${new Intl.NumberFormat().format(monthlyStats.investment.increaseAmount)}` : `-$${new Intl.NumberFormat().format(Math.abs(monthlyStats.investment.increaseAmount))}`}
                    icon={<DollarSign className="h-8 w-8 text-white" />}
                    color="text-green-500"
                    bgColor="bg-gradient-to-br from-green-500 to-green-600"
                  />
                  
                  <CounterCard
                    title="Active KOLs"
                    value={isLoading ? "..." : new Set(filteredData.map(item => item.kol_db_id)).size.toString()}
                    change={monthlyStats.kols.percentChange}
                    increaseAmount={monthlyStats.kols.increaseAmount >= 0 ? `+${monthlyStats.kols.increaseAmount}` : monthlyStats.kols.increaseAmount.toString()}
                    icon={<Users className="h-8 w-8 text-white" />}
                    color="text-purple-500"
                    bgColor="bg-gradient-to-br from-purple-500 to-purple-600"
                  />
                  
                  <CounterCard
                    title="Total Postings"
                    value={isLoading ? "..." : filteredData.length.toString()}
                    change={monthlyStats.postings.percentChange}
                    increaseAmount={monthlyStats.postings.increaseAmount >= 0 ? `+${monthlyStats.postings.increaseAmount}` : monthlyStats.postings.increaseAmount.toString()}
                    icon={<Target className="h-8 w-8 text-white" />}
                    color="text-orange-500"
                    bgColor="bg-gradient-to-br from-orange-500 to-orange-600"
                  />
                </div>

                {/* Regional Hierarchical Data Table */}
                <RegionalHierarchicalTable 
                  data={filteredData} 
                  isLoading={isLoading}
                />
                
              </div>
            </TabsContent>

            {hasFullAccess && (
              <TabsContent value="twitter-reach" className="mt-0">
                <Tabs defaultValue="dashboard" onValueChange={(value) => setTwitterTab(value as any)} className="w-full">
                  <TabsList className="mb-6">
                    <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                    <TabsTrigger value="kolList">KOL Dictionary</TabsTrigger>
                    <TabsTrigger value="campaigns">KOL Campaigns</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="dashboard" className="mt-0">
                    <TwitterKolDashboard />
                  </TabsContent>
                  
                  <TabsContent value="kolList" className="mt-0">
                    <TwitterKolList />
                  </TabsContent>
                  
                  <TabsContent value="campaigns" className="mt-0">
                    {activeTwitterView === "list" ? (
                      <TwitterCampaignList onCampaignSelect={handleCampaignSelect} />
                    ) : (
                      <TwitterCampaignDetail 
                        campaignId={selectedCampaignId || ""} 
                        onBack={handleBackToList} 
                      />
                    )}
                  </TabsContent>
                </Tabs>
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default GlobalReach;
